/**
 * Sets the absolute transparency of a color.
 * Any existing alpha values are overwritten.
 * @param {string} color - CSS color, i.e. one of: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color()
 * @param {number} value - value to set the alpha channel to in the range 0 - 1
 * @returns {string} A CSS color string. Hex input values are returned as rgb
 */
export const alpha = (color: string, value: number): string => {
  const clampedValue = Math.max(0, Math.min(1, value));

  // Handle #nnn and #nnnn hex colors
  if (/^#([0-9a-f]{3,4})$/i.test(color)) {
    const hex = color.replace('#', '');
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${clampedValue})`;
  }

  // Handle #nnnnnn and #nnnnnnnn hex colors
  if (/^#([0-9a-f]{6,8})$/i.test(color)) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${clampedValue})`;
  }

  // Handle rgb/rgba colors
  if (/^rgba?\(/.test(color)) {
    return color.replace(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(,\s*[\d.]+)?\)/,
      (_, r, g, b) => `rgba(${r}, ${g}, ${b}, ${clampedValue})`,
    );
  }

  // Handle hsl/hsla colors
  if (/^hsla?\(/.test(color)) {
    return color.replace(
      /hsla?\((\d+),\s*(\d+)%,\s*(\d+)%(,\s*[\d.]+)?\)/,
      (_, h, s, l) => `hsla(${h}, ${s}%, ${l}%, ${clampedValue})`,
    );
  }

  throw new Error('Unsupported color format');
};
